import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { beheerVakmannen, beoordeelReclamatie, boekVakmanTegoed, zetVakmanStatus } from "@/lib/vakman.functions";
import { Logo, PAGE_CONTAINER } from "../../components/site";
import { euro } from "../../components/vakman";
import { OPVOLG_OPTIES } from "@/lib/vakman-shared";

export const Route = createFileRoute("/_authenticated/vakmannen-beheer")({
  head: () => ({
    meta: [
      { title: "Vakmannen beheren | offerto" },
      { name: "description", content: "Beheer vakmannen, aankopen en reclamaties." },
      { property: "og:title", content: "Vakmannen beheren | offerto" },
      { property: "og:description", content: "Beheer vakmannen, aankopen en reclamaties." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Beheer,
});

const STATUS: Record<string, string> = { in_afwachting: "Wacht op goedkeuring", actief: "Actief", gepauzeerd: "Gepauzeerd", geweigerd: "Geweigerd" };

function Beheer() {
  const haal = useServerFn(beheerVakmannen);
  const zetStatus = useServerFn(zetVakmanStatus);
  const boek = useServerFn(boekVakmanTegoed);
  const beoordeel = useServerFn(beoordeelReclamatie);
  const { data, isPending, isError, refetch } = useQuery({ queryKey: ["beheer-vakmannen"], queryFn: () => haal() });
  const [open, setOpen] = useState<string | null>(null);
  const [bedrag, setBedrag] = useState("");

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className={`${PAGE_CONTAINER} flex items-center justify-between gap-4 py-4`}>
          <Logo />
          <Link to="/admin" className="rounded-md border border-border px-4 py-2 text-sm font-bold">Terug naar beheer</Link>
        </div>
      </header>
      <main className={`${PAGE_CONTAINER} py-8`}>
        <h1 className="text-2xl font-bold">Vakmannen</h1>
        <p className="mt-1 text-sm text-muted-foreground">Aanmelding vakmannen: /vakman/aanmelden · Portaal: /vakman</p>
        {isPending && <p className="mt-6 text-muted-foreground">Laden…</p>}
        {isError && <p className="mt-6 text-destructive">Geen toegang of laden mislukt.</p>}
        {data && data.vakmannen.length === 0 && <p className="mt-6 text-muted-foreground">Nog geen aanmeldingen.</p>}
        <div className="mt-6 space-y-4">
          {data?.vakmannen.map((v) => {
            const aankopen = data.aankopen.filter((a) => a.vakman_id === v.id);
            const recs = data.reclamaties.filter((r) => r.vakman_id === v.id);
            const beantwoord = aankopen.filter((a) => a.opvolgstatus).length;
            return (
              <div key={v.id} className="rounded-xl border border-border p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-lg font-bold">{v.bedrijfsnaam}</p>
                    <p className="text-sm text-muted-foreground">{v.voornaam} {v.achternaam} · {v.email} · {v.telefoon}{v.kvk ? ` · KvK ${v.kvk}` : ""}</p>
                    <p className="text-sm text-muted-foreground">{v.plaats} + {v.straal_km} km · {v.categorieen.join(", ")}</p>
                  </div>
                  <div className="text-right text-sm">
                    <span className="rounded-md bg-secondary px-2.5 py-1 font-bold">{STATUS[v.status] ?? v.status}</span>
                    <p className="mt-2">Tegoed <strong>{euro(v.saldo)}</strong></p>
                    <p>{aankopen.length} gekocht · {beantwoord} beantwoord</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-sm">
                  {v.status !== "actief" && <button onClick={() => void zetStatus({ data: { id: v.id, status: "actief" } }).then(() => refetch())} className="rounded-md bg-primary px-3 py-1.5 font-bold text-primary-foreground">Goedkeuren</button>}
                  {v.status === "actief" && <button onClick={() => void zetStatus({ data: { id: v.id, status: "gepauzeerd" } }).then(() => refetch())} className="rounded-md border border-border px-3 py-1.5 font-bold">Pauzeren</button>}
                  {v.status === "in_afwachting" && <button onClick={() => void zetStatus({ data: { id: v.id, status: "geweigerd" } }).then(() => refetch())} className="rounded-md border border-border px-3 py-1.5 font-bold text-destructive">Weigeren</button>}
                  <button onClick={() => setOpen(open === v.id ? null : v.id)} className="rounded-md border border-border px-3 py-1.5 font-bold">{open === v.id ? "Sluiten" : "Aanvragen & tegoed"}</button>
                </div>
                {open === v.id && (
                  <div className="mt-4 space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <input value={bedrag} onChange={(e) => setBedrag(e.target.value)} placeholder="Bedrag, bijv. 90" inputMode="decimal" className="h-10 w-40 rounded-md border border-input px-3" />
                      <button onClick={() => { const n = Number(bedrag.replace(",", ".")); if (n) void boek({ data: { id: v.id, bedrag: n, omschrijving: "Tegoed bijgeboekt door beheer" } }).then(() => { setBedrag(""); void refetch(); }); }}
                        className="rounded-md bg-primary px-3 py-2 text-sm font-bold text-primary-foreground">Tegoed bijboeken</button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-muted-foreground"><th className="py-2 pr-3">Datum</th><th className="pr-3">Klus</th><th className="pr-3">Plaats</th><th className="pr-3">Klant</th><th className="pr-3">Beantwoord</th><th>Reclamatie</th></tr></thead>
                        <tbody>
                          {aankopen.length === 0 && <tr><td colSpan={6} className="py-2 text-muted-foreground">Nog geen aanvragen gekocht.</td></tr>}
                          {aankopen.map((a: any) => {
                            const r = recs.find((x) => x.aankoop_id === a.id);
                            return (
                              <tr key={a.id} className="border-t border-border align-top">
                                <td className="py-2 pr-3">{new Date(a.created_at).toLocaleDateString("nl-NL")}</td>
                                <td className="pr-3">{a.leads?.type_opdracht ?? "—"}</td>
                                <td className="pr-3">{a.leads?.postcode} {a.leads?.plaats}</td>
                                <td className="pr-3">{a.leads?.voornaam} {a.leads?.achternaam}</td>
                                <td className="pr-3">{a.opvolgstatus ? OPVOLG_OPTIES[a.opvolgstatus as keyof typeof OPVOLG_OPTIES] : "Nee"}</td>
                                <td>
                                  {!r ? "—" : (
                                    <div>
                                      <p><strong>{r.reden}</strong> ({r.status})</p>
                                      {r.toelichting && <p className="text-muted-foreground">{r.toelichting}</p>}
                                      {r.status === "open" && (
                                        <div className="mt-1 flex gap-2">
                                          <button onClick={() => void beoordeel({ data: { id: r.id, goedkeuren: true } }).then(() => refetch())} className="font-bold text-primary underline">Goedkeuren (+€18)</button>
                                          <button onClick={() => void beoordeel({ data: { id: r.id, goedkeuren: false } }).then(() => refetch())} className="font-bold text-destructive underline">Afwijzen</button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
